/**
 * ConnectionHealthBanner
 * ──────────────────────
 * Red alert banner shown on top of the Clients page when the workspace has a
 * stale Google Ads OAuth (only real per-workspace integration that breaks).
 *
 * The Google Ads `refresh_token` is workspace-global (in `app_settings`),
 * not per-client — so when it's missing, NO client can pull SEA data.
 * Click the "Reconnecter" button → starts the OAuth flow (redirect to Google).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, X, ArrowRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConnectionHealthBanner() {
  const [issues, setIssues] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients/connection-health/summary`, { withCredentials: true });
      setIssues(data.issues || []);
    } catch {
      // silent — banner is purely informational
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const reconnect = async (issue) => {
    if (issue.provider !== 'google_ads') return;
    setReconnecting(true);
    try {
      const { data } = await axios.get(`${API}/google-ads/oauth/start`, { withCredentials: true });
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      toast.error('Impossible de démarrer la reconnexion.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de reconnexion');
    } finally {
      setReconnecting(false);
    }
  };

  if (!issues.length || dismissed) return null;

  return (
    <div className="border border-red-200 bg-red-50 rounded-lg p-3 space-y-2" data-testid="connection-health-banner">
      {issues.map((iss, idx) => (
        <div key={idx} className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-red-900 text-sm">
              {iss.label} — {iss.reason}
            </div>
            <div className="text-[12px] text-red-800/80 mt-0.5">
              {iss.affected_clients_count} client{iss.affected_clients_count > 1 ? 's' : ''} dépend{iss.affected_clients_count > 1 ? 'ent' : ''} de cette connexion. {iss.fix_action}
            </div>
          </div>
          {iss.provider === 'google_ads' && (
            <button
              type="button"
              onClick={() => reconnect(iss)}
              disabled={reconnecting}
              className="text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium px-3 py-1.5 rounded inline-flex items-center gap-1 shrink-0"
              data-testid="reconnect-btn"
            >
              {reconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              Reconnecter
            </button>
          )}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-red-400 hover:text-red-700 shrink-0"
            title="Masquer"
            data-testid="health-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
